import { left, right } from '@sweet-monads/either';
import type { Either } from '@sweet-monads/either';

import { AbstractError } from '@/domain/errors/abstract-error.js';
import { ServerError } from '@/domain/errors/server-error.js';
import type {
    AiAnalysisProgressModel,
    AuditDataModel,
    CancelDevelopmentParams,
    CreateDevelopmentParams,
    CreateEstimateParams,
    CreateSolicitationParams,
    DevelopmentDetailDataModel,
    DevelopmentDetailDocumentResponse,
    DevelopmentDocumentModel,
    DevelopmentSummaryModel,
    EfVersionModel,
    ImpedeDevelopmentParams,
    JiraEpicLink,
    JiraEpicSuggestion,
    JiraNoPairResult,
    PbiDecompositionModel,
    StartDevelopmentParams,
    UpdateDevelopmentParams,
    ValidationEvidenceModel
} from '@/domain/models/development.js';
import type { SolicitationEvidenceModel } from '@/domain/models/solicitation-evidence.js';
import type { DevelopmentRepository } from '@/domain/repositories/development-repository.js';
import type {
    ApproveDocumentation,
    ApproveEstimate,
    ApproveSolicitation,
    ApproveUcValidation,
    AssignTechLead,
    CancelDevelopment,
    CloseWithoutUc,
    ConfirmNoJiraPair,
    CreateDevelopment,
    CreateEstimate,
    CreateSolicitation,
    DeleteDevelopment,
    DeleteEf,
    DownloadCfEvidence,
    DownloadDocument,
    DownloadEf,
    DownloadUcEvidence,
    ExportDevelopmentsExcel,
    GetAiAnalysisProgress,
    GetPbiDecomposition,
    GetSolicitationEvidenceDownloadUrl,
    ImpedeDevelopment,
    ImportDevelopmentsExcel,
    ImportDevelopmentsResult,
    LinkJiraEpic,
    ListCfEvidences,
    ListJiraEpics,
    ListSolicitationEvidences,
    ListUcEvidences,
    LoadAudit,
    LoadDevelopmentById,
    LoadDevelopments,
    MakeDeveloperLead,
    ReactivateDevelopment,
    ReclassifySolicitation,
    RegisterDevEnd,
    RejectDocumentation,
    RejectEstimate,
    RejectSolicitation,
    ReleaseToUc,
    RemoveDeveloper,
    RemoveSolicitationEvidence,
    RemoveValidationEvidence,
    RequestAiAnalysis,
    ResolveSolicitation,
    ResumeDevelopment,
    RetryJiraSync,
    RetryPbiDecomposition,
    ReturnEstimateToAnalysis,
    SendToDevelopment,
    SetDevelopers,
    StartDevelopment,
    SubmitEstimate,
    ToggleCutoverNotRequired,
    UpdateDevelopment,
    UploadDocument,
    UploadEf,
    UploadSolicitationEvidence,
    UploadValidationEvidence
} from '@/domain/use-cases/developments.js';

async function safeVoid(fn: () => Promise<void>): Promise<{ isLeft: true; error: AbstractError } | { isLeft: false; value: undefined }> {
    try {
        await fn();
        return { isLeft: false, value: undefined };
    } catch (error) {
        if (error instanceof AbstractError) {
            return { isLeft: true, error };
        }
        return { isLeft: true, error: new ServerError((error as Error).message) };
    }
}

export class ExportDevelopmentsExcelUseCase implements ExportDevelopmentsExcel {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(params?: {
        search?: string;
        statuses?: string[];
        projectId?: string;
        includeCanceled?: boolean;
    }): Promise<Either<AbstractError, { blob: Blob; filename: string }>> {
        try {
            return right(await this.repository.exportExcel(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ImportDevelopmentsExcelUseCase implements ImportDevelopmentsExcel {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(file: File, projectId: string): Promise<Either<AbstractError, ImportDevelopmentsResult>> {
        try {
            return right(await this.repository.importExcel(file, projectId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadDevelopmentsUseCase implements LoadDevelopments {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(params?: {
        search?: string;
        statuses?: string[];
        projectId?: string;
        page?: number;
        limit?: number;
        includeCanceled?: boolean;
    }): Promise<Either<AbstractError, { data: DevelopmentSummaryModel[]; total: number; page: number; totalPages: number }>> {
        try {
            return right(await this.repository.findAll(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadDevelopmentByIdUseCase implements LoadDevelopmentById {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, DevelopmentDetailDataModel>> {
        try {
            return right(await this.repository.findById(id));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CreateDevelopmentUseCase implements CreateDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(params: CreateDevelopmentParams): Promise<Either<AbstractError, { id: string; jiraWarning: string | null }>> {
        try {
            return right(await this.repository.create(params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UpdateDevelopmentUseCase implements UpdateDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string, params: UpdateDevelopmentParams): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.update(id, params);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class CancelDevelopmentUseCase implements CancelDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string, params: CancelDevelopmentParams): Promise<Either<AbstractError, void>> {
        try {
            await this.repository.cancel(id, params);
            return right(undefined);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ReactivateDevelopmentUseCase implements ReactivateDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.reactivate(id));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ImpedeDevelopmentUseCase implements ImpedeDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string, params: ImpedeDevelopmentParams): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.impede(id, params));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ResumeDevelopmentUseCase implements ResumeDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.resume(id));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class DeleteDevelopmentUseCase implements DeleteDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.delete(id));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class StartDevelopmentUseCase implements StartDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(id: string, params: StartDevelopmentParams): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.start(id, params));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class UploadEfUseCase implements UploadEf {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, file: File): Promise<Either<AbstractError, EfVersionModel>> {
        try {
            return right(await this.repository.uploadEf(developmentId, file));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DeleteEfUseCase implements DeleteEf {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        try {
            return right(await this.repository.deleteEf(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DownloadEfUseCase implements DownloadEf {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, version?: number): Promise<Either<AbstractError, { blob: Blob; filename: string }>> {
        try {
            return right(await this.repository.downloadEf(developmentId, version));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RequestAiAnalysisUseCase implements RequestAiAnalysis {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, { attemptNumber: number }>> {
        try {
            const value = await this.repository.requestAiAnalysis(developmentId);
            return right(value);
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class SendToDevelopmentUseCase implements SendToDevelopment {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.sendToDevelopment(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class CreateEstimateUseCase implements CreateEstimate {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, params: CreateEstimateParams): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.createEstimate(developmentId, params));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ApproveEstimateUseCase implements ApproveEstimate {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.approveEstimate(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class RejectEstimateUseCase implements RejectEstimate {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, reasonId: string, notes?: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.rejectEstimate(developmentId, reasonId, notes));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class SubmitEstimateUseCase implements SubmitEstimate {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.submitEstimate(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ReturnEstimateToAnalysisUseCase implements ReturnEstimateToAnalysis {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, reasonId: string, notes?: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.returnEstimateToAnalysis(developmentId, reasonId, notes));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class AssignTechLeadUseCase implements AssignTechLead {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, techLeadId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.assignTechLead(developmentId, techLeadId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class SetDevelopersUseCase implements SetDevelopers {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, developers: Array<{ userId: string; isLead: boolean }>): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.setDevelopers(developmentId, developers));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class RemoveDeveloperUseCase implements RemoveDeveloper {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, developerId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.removeDeveloper(developmentId, developerId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class MakeDeveloperLeadUseCase implements MakeDeveloperLead {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, developerId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.makeDeveloperLead(developmentId, developerId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class UploadDocumentUseCase implements UploadDocument {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, type: string, file: File): Promise<Either<AbstractError, DevelopmentDocumentModel>> {
        try {
            return right(await this.repository.uploadDocument(developmentId, type, file));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DownloadDocumentUseCase implements DownloadDocument {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, documentId: string): Promise<Either<AbstractError, { url: string }>> {
        try {
            return right(await this.repository.downloadDocument(developmentId, documentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RegisterDevEndUseCase implements RegisterDevEnd {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, endDate: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.finishDev(developmentId, endDate));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ToggleCutoverNotRequiredUseCase implements ToggleCutoverNotRequired {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, cutoverNotRequired: boolean): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.toggleCutoverNotRequired(developmentId, cutoverNotRequired));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ApproveDocumentationUseCase implements ApproveDocumentation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.approveDocumentation(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class RejectDocumentationUseCase implements RejectDocumentation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, reasonId: string, notes?: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.rejectDocumentation(developmentId, reasonId, notes));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class UploadValidationEvidenceUseCase implements UploadValidationEvidence {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, file: File, type: 'cf' | 'uc'): Promise<Either<AbstractError, ValidationEvidenceModel>> {
        try {
            return right(await this.repository.uploadValidationEvidence(developmentId, file, type));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RemoveValidationEvidenceUseCase implements RemoveValidationEvidence {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, type: 'cf' | 'uc', evidenceId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.removeValidationEvidence(developmentId, type, evidenceId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ListCfEvidencesUseCase implements ListCfEvidences {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, DevelopmentDetailDocumentResponse[]>> {
        try {
            return right(await this.repository.listCfEvidences(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ListUcEvidencesUseCase implements ListUcEvidences {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, DevelopmentDetailDocumentResponse[]>> {
        try {
            return right(await this.repository.listUcEvidences(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DownloadCfEvidenceUseCase implements DownloadCfEvidence {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, docId: string): Promise<Either<AbstractError, { url: string }>> {
        try {
            return right(await this.repository.downloadCfEvidence(developmentId, docId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class DownloadUcEvidenceUseCase implements DownloadUcEvidence {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, docId: string): Promise<Either<AbstractError, { url: string }>> {
        try {
            return right(await this.repository.downloadUcEvidence(developmentId, docId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ReleaseToUcUseCase implements ReleaseToUc {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.releaseToUc(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ApproveUcValidationUseCase implements ApproveUcValidation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.approveUcValidation(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class CloseWithoutUcUseCase implements CloseWithoutUc {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.approveWithoutUc(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class CreateSolicitationUseCase implements CreateSolicitation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, params: CreateSolicitationParams): Promise<Either<AbstractError, { id: string }>> {
        try {
            return right(await this.repository.createSolicitation(developmentId, params));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class ApproveSolicitationUseCase implements ApproveSolicitation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string): Promise<Either<AbstractError, { jiraWarning: string | null }>> {
        try {
            return right(await this.repository.approveSolicitation(developmentId, solicitationId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RejectSolicitationUseCase implements RejectSolicitation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string, reasonId: string, notes?: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.rejectSolicitation(developmentId, solicitationId, reasonId, notes));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ResolveSolicitationUseCase implements ResolveSolicitation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string, notes: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.resolveSolicitation(developmentId, solicitationId, notes));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ReclassifySolicitationUseCase implements ReclassifySolicitation {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string, newType: string, notes: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.reclassifySolicitation(developmentId, solicitationId, newType, notes));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ListSolicitationEvidencesUseCase implements ListSolicitationEvidences {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string): Promise<Either<AbstractError, SolicitationEvidenceModel[]>> {
        try {
            return right(await this.repository.listSolicitationEvidences(developmentId, solicitationId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class UploadSolicitationEvidenceUseCase implements UploadSolicitationEvidence {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string, file: File): Promise<Either<AbstractError, SolicitationEvidenceModel>> {
        try {
            return right(await this.repository.uploadSolicitationEvidence(developmentId, solicitationId, file));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RemoveSolicitationEvidenceUseCase implements RemoveSolicitationEvidence {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string, docId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.removeSolicitationEvidence(developmentId, solicitationId, docId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class GetSolicitationEvidenceDownloadUrlUseCase implements GetSolicitationEvidenceDownloadUrl {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, solicitationId: string, docId: string): Promise<Either<AbstractError, { url: string }>> {
        try {
            return right(await this.repository.getSolicitationEvidenceDownloadUrl(developmentId, solicitationId, docId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LoadAuditUseCase implements LoadAudit {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, AuditDataModel>> {
        try {
            return right(await this.repository.loadAudit(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class GetPbiDecompositionUseCase implements GetPbiDecomposition {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, PbiDecompositionModel | null>> {
        try {
            return right(await this.repository.getPbiDecomposition(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class GetAiAnalysisProgressUseCase implements GetAiAnalysisProgress {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, AiAnalysisProgressModel | null>> {
        try {
            return right(await this.repository.getAiAnalysisProgress(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RetryPbiDecompositionUseCase implements RetryPbiDecomposition {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.retryPbiDecomposition(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ListJiraEpicsUseCase implements ListJiraEpics {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, JiraEpicSuggestion>> {
        try {
            return right(await this.repository.listJiraEpics(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class LinkJiraEpicUseCase implements LinkJiraEpic {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string, epicKey: string): Promise<Either<AbstractError, JiraEpicLink>> {
        try {
            return right(await this.repository.linkJiraEpic(developmentId, epicKey));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}

export class RetryJiraSyncUseCase implements RetryJiraSync {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, void>> {
        const result = await safeVoid(() => this.repository.retryJira(developmentId));
        return result.isLeft ? left(result.error) : right(result.value);
    }
}

export class ConfirmNoJiraPairUseCase implements ConfirmNoJiraPair {
    constructor(private readonly repository: DevelopmentRepository) {}

    public async execute(developmentId: string): Promise<Either<AbstractError, JiraNoPairResult>> {
        try {
            return right(await this.repository.confirmNoJiraPair(developmentId));
        } catch (error) {
            if (error instanceof AbstractError) {
                return left(error);
            }
            return left(new ServerError((error as Error).message));
        }
    }
}
